import { io } from "socket.io-client";
import axios from 'axios';
import { toast } from 'react-toastify';
import baseURL from '../utils/baseURL';

let socket = null;
// Guards a re-auth attempt so a genuinely forbidden user cannot loop on it.
let reauthInFlight = false;
// After a SUCCESSFUL re-auth, hand a repeated auth error to the page instead
// of refreshing again: a signed token that still resolves to no support
// identity (deleted row, unknown role) would otherwise loop refresh → retry →
// error at network speed, rotating the refresh token every pass.
const REAUTH_COOLDOWN_MS = 30 * 1000;
let reauthCooldownUntil = 0;
// Set by callers that want to retry their last request once the socket regains
// its identity — otherwise a successful re-auth would fix the socket and leave
// the screen still empty.
let onReauthenticated = null;
// The page's supportError handler. The service owns the ONE real socket
// listener (it has to see auth errors first); the page only gets what is left.
let pageSupportErrorHandler = null;
// Page callbacks to run after a RE-connect (never the first connect).
const reconnectHandlers = new Set();
// socket.io-client v4 emits `reconnect` on the Manager, not the Socket, so the
// only reliable "we are back" signal at socket level is a second `connect`.
let connectCount = 0;

/**
 * Give the LIVE socket a fresh identity.
 *
 * The socket's identity comes from the token presented at handshake, and access
 * tokens last about fifteen minutes. A tab left open therefore ends up with a
 * socket that is connected but anonymous, and every role-guarded event answers
 * Forbidden while the connection itself looks perfectly healthy.
 *
 * Refreshing and re-authenticating in place is deliberate: reconnecting would
 * work, but it would also drop chat and support rooms that have nothing to do
 * with the expired token.
 *
 * Uses a bare axios call rather than the app's authed instance on purpose — that
 * instance refreshes on 401, and calling it from inside a refresh is how you get
 * two refreshes racing each other.
 */
async function refreshAndReauthenticate() {
    try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken || !socket) return false;

        const res = await axios.post(`${baseURL}/auth/refresh`, { refreshToken });
        const { accessToken, refreshToken: newRefresh } = res.data?.data || {};
        if (!accessToken) return false;

        localStorage.setItem('accessToken', accessToken);
        if (newRefresh) localStorage.setItem('refreshToken', newRefresh);

        // Keep the handshake copies current too, so a later reconnect carries
        // the fresh token rather than the one that just expired.
        socket.auth = { ...(socket.auth || {}), token: accessToken };
        // Never in the URL — see "NO SECRETS IN THE URL" below.
        if (socket.io?.opts?.query) {
            const { token: _dropToken, fcmToken: _dropFcm, ...rest } = socket.io.opts.query;
            socket.io.opts.query = rest;
        }

        return await new Promise((resolve) => {
            let settled = false;
            const done = (v) => { if (!settled) { settled = true; resolve(v); } };
            // If the server is older than this event, the ack never comes —
            // resolve false rather than hanging the caller forever.
            setTimeout(() => done(false), 5000);
            socket.emit('authenticate', { token: accessToken }, (r) => done(Boolean(r?.ok)));
        });
    } catch (err) {
        console.error('🔑 Support socket re-authentication failed:', err?.message);
        return false;
    }
}

/** Register what to re-run once the socket regains its identity. */
export const setOnReauthenticated = (fn) => { onReauthenticated = typeof fn === 'function' ? fn : null; };

// 'Forbidden' is the old wording; the codes are what the server sends now
// (from requestUserTickets, the connection-time join, and the ticket events)
// when the token it was handed has expired.
const isAuthError = (error) =>
    error?.message === 'Forbidden'
    || error?.code === 'SUPPORT_UNAUTHENTICATED'
    || error?.code === 'UNAUTHENTICATED';

/**
 * A CONNECTED SOCKET CAN STILL BE UNAUTHENTICATED.
 *
 * socket.user is set from the token presented at HANDSHAKE. Access tokens
 * last ~15 minutes and a socket lives as long as the tab, so any page left
 * open long enough (or opened after sitting idle elsewhere) ends up connected
 * but anonymous — and every guarded event answers with an auth error while
 * the socket looks perfectly healthy.
 *
 * So: refresh the token and re-authenticate ON THE SAME CONNECTION, then
 * re-request tickets and let the page re-join whatever it had open.
 * Reconnecting instead would drop chat. Once per error, so a genuinely
 * forbidden user cannot loop.
 *
 * Returns true when the error was an auth error (handled here, or already
 * being handled) so the caller does not surface it to the user.
 */
function handleAuthError(error) {
    if (!isAuthError(error)) return false;
    if (Date.now() < reauthCooldownUntil) return false;
    if (reauthInFlight) return true;

    reauthInFlight = true;
    refreshAndReauthenticate()
        .then((ok) => {
            if (ok) {
                console.log('🔑 Support socket re-authenticated');
                reauthCooldownUntil = Date.now() + REAUTH_COOLDOWN_MS;
                if (typeof onReauthenticated === 'function') {
                    onReauthenticated();
                } else if (socket?.connected) {
                    socket.emit('requestUserTickets');
                }
            } else if (error?.message === 'Forbidden') {
                // The signed token does not carry the role this queue needs —
                // almost always a stale session after a role change.
                toast.warning('You do not have access to this support queue.');
            } else {
                toast.warning('Your session has expired. Please sign in again to use support.');
            }
        })
        .finally(() => { reauthInFlight = false; });
    return true;
}
let isConnecting = false;
let connectionRole = null; // Track which role connected
let connectionId = null; // Track which user ID connected
let socketWarningShown = false; // Track if we've already warned about missing socket to avoid spam

// Store callbacks for listeners that need to be attached when socket connects
const pendingCallbacks = {
    user: {
        receiveMessage: null
    }
};

// Get socket URL from environment - use VITE_WS_BASE_URL or fallback to API base URL
const getSocketUrl = () => {
    // Try VITE_WS_BASE_URL first (for WebSocket-specific URL)
    let wsBaseUrl = import.meta.env.VITE_WS_BASE_URL;

    // Fallback to VITE_API_BASE_URL if WS_BASE_URL is not set
    if (!wsBaseUrl) {
        wsBaseUrl = import.meta.env.VITE_API_BASE_URL;
    }

    // Final fallback to default development URL
    if (!wsBaseUrl) {
        wsBaseUrl = 'http://feelbetter-dev.it-mitra.com/api';
        console.warn('⚠️ Socket: Using default WebSocket URL. Set VITE_WS_BASE_URL or VITE_API_BASE_URL in .env file.');
    }

    // Remove /api suffix if present and trim any whitespace
    const socketUrl = wsBaseUrl.trim().replace(/\/api\/?$/, '');

    console.log('🔌 Socket URL:', socketUrl);

    return socketUrl;
};

/**
 * Connect socket with role-based authentication
 * @param {Object} params - Connection parameters
 * @param {string} params.role - User role (admin, doctor, user = staff, patient) — every one of them raises tickets
 * @param {string} params.id - User ID
 * @returns {Object} Socket instance
 */
export const connectSupportSocket = ({ role, id }) => {
    // Check if user is authenticated before creating socket
    const userId = localStorage.getItem('ID');
    const userRole = localStorage.getItem('Role');
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';

    // Don't create socket if user is not authenticated
    if (!userId || !userRole || !isAuthenticated) {
        console.log('📵 Support socket: User not authenticated, skipping connection');
        return null;
    }

    // Validate that the provided role and id match the authenticated user
    if (role !== userRole || id !== userId) {
        console.warn('⚠️ Support socket: Role/ID mismatch with authenticated user');
        return null;
    }

    // If socket exists and is connected with the same role/id, return it
    if (socket?.connected && connectionRole === role && connectionId === id) {
        console.log('✅ Support socket already connected:', socket.id);
        return socket;
    }

    // If trying to connect with different user, disconnect first
    if (socket && (connectionRole !== role || connectionId !== id)) {
        console.log('🔄 Different user detected, disconnecting old socket');
        socket.disconnect();
        socket = null;
    }

    // If connection is in progress, wait
    if (isConnecting) {
        console.log('⏳ Support socket connection in progress');
        return socket;
    }

    // If socket exists but is disconnected, reconnect it
    if (socket && !socket.connected) {
        console.log('🔄 Support socket exists but disconnected, reconnecting...');
        socket.connect();
        connectionRole = role;
        connectionId = id;
        return socket;
    }

    // Create new socket connection
    console.log('🆕 Creating NEW socket connection for:', { role, id });
    isConnecting = true;
    connectionRole = role;
    connectionId = id;
    socketWarningShown = false; // Reset warning flag when creating new socket
    connectCount = 0;

    try {
        const socketUrl = getSocketUrl();

        if (!socketUrl) {
            console.error('❌ Cannot create socket: No valid socket URL available');
            isConnecting = false;
            toast.warning('Support service configuration error. Please contact support.');
            return null;
        }

        console.log('🔌 Connecting support socket to:', socketUrl);

        // THE TOKEN is the socket's identity. Without it socketAuthMiddleware
        // leaves `socket.user` unset and the server refuses every support event:
        // it derives who is raising (admin / doctor / staff / patient, and their
        // tenant) from the verified claims, never from the ids below.
        //
        // The role ids in the query are a legacy hint — anyone can type one, so
        // the server treats them as presence-only once a verified user exists.
        const accessToken = localStorage.getItem('accessToken');

        const queryParams = {};
        if (role === 'admin') {
            queryParams.adminId = id;
        } else if (role === 'doctor') {
            queryParams.doctorId = id;
        } else if (role === 'patient') {
            queryParams.patientId = id;
        }

        // NO SECRETS IN THE URL (2 Oct 2026). The access token travels ONLY in
        // `auth` (Socket.IO's CONNECT packet, never part of the address) — the
        // server reads auth.token first (socket/socketAuth.js). It used to be
        // copied into the query too, so every proxy access log held a live
        // token. The FCM device token is not read from the socket at all (push
        // registration goes through the REST API), so it is not sent either.

        // Socket.IO configuration
        // Uses default /socket.io/ path (matches Apache proxy: /socket.io/ → backend:5000/socket.io/)
        // Apache handles WebSocket upgrade via ProxyPass with upgrade=WebSocket
        console.log('🔨 Creating new Socket.IO instance with config:', {
            url: socketUrl,
            queryParams,
            transports: ["websocket", "polling"]
        });

        socket = io(socketUrl, {
            // The token goes in `auth` ONLY: it rides in Socket.IO's CONNECT
            // packet on websocket and polling alike, and never becomes part of
            // the URL a proxy logs (2 Oct 2026 — it used to be copied into the
            // query too).
            auth: accessToken ? { token: accessToken } : undefined,
            // A fresh Manager every time. socket.io-client caches Managers per
            // URL and would otherwise hand a re-entered page the OLD one —
            // stale handshake query, plus our Manager-level listeners below
            // stacking up on every visit.
            forceNew: true,
            // No path option = use default /socket.io/ (matches Apache config)
            transports: ["websocket", "polling"],  // WebSocket first, fallback to polling
            upgrade: true,
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 20000,
            query: queryParams  // Backend reads from socket.handshake.query
        });

        console.log('✅ Socket.IO instance created:', socket);
        console.log('🔌 Socket connecting state:', socket.connecting);
        console.log('🔌 Socket connected state:', socket.connected);

        socket.on("connect", () => {
            connectCount += 1;
            const isReconnect = connectCount > 1;
            console.log("✅ Support socket connected:", socket.id, isReconnect ? '(reconnect)' : '');
            console.log(`📡 Connected as ${role} with ID: ${id}`);
            console.log(`🔌 Transport:`, socket.io.engine.transport.name); // Shows 'polling' or 'websocket'
            isConnecting = false;

            // Attach any pending listeners
            if (pendingCallbacks.user.receiveMessage) {
                console.log('🎧 Attaching pending receiveSupportMessage listener');
                socket.off('receiveSupportMessage');
                socket.on('receiveSupportMessage', pendingCallbacks.user.receiveMessage);
                pendingCallbacks.user.receiveMessage = null;
            }

            // The server emits `userTickets` on connection for every raiser
            // (admin, doctor, staff and patient alike); the page's listener is
            // expected to be attached already. No 'supportConnection' event.

            // After a RE-connect the rooms are gone with the old connection:
            // ask for the ticket list again and let the page re-join the
            // ticket it has open. (First connect is covered by the page.)
            if (isReconnect) {
                console.log('🔄 Re-requesting user tickets after reconnect');
                socket.emit('requestUserTickets');
                reconnectHandlers.forEach((fn) => {
                    try { fn(); } catch (e) { console.error('reconnect handler failed:', e); }
                });
            }
        });

        // The ONE real supportError listener. Auth errors are recovered here
        // (see handleAuthError) and never reach the page; everything else is
        // forwarded to whatever the page registered via onSupportError.
        socket.on('supportError', (data) => {
            console.log('📥 LISTEN: supportError', data);
            if (handleAuthError(data)) return;
            if (typeof pageSupportErrorHandler === 'function') pageSupportErrorHandler(data);
        });

        // Log when transport upgrades to WebSocket
        socket.io.engine.on("upgrade", () => {
            console.log("⬆️ Upgraded to:", socket.io.engine.transport.name);
        });

        socket.on("disconnect", (reason) => {
            console.log("❌ Support socket DISCONNECTED:", reason);
            console.log("❌ Disconnect reason details:", {
                reason,
                timestamp: new Date().toISOString(),
                socketId: socket?.id,
                role: connectionRole,
                userId: connectionId
            });
            isConnecting = false;

            // Show user-friendly message for unexpected disconnections
            if (reason === 'io server disconnect') {
                toast.warning('Support service disconnected. Reconnecting...');
            } else if (reason === 'transport close' || reason === 'transport error') {
                toast.warning('Connection lost. Attempting to reconnect...');
            }
        });

        socket.on("connect_error", (err) => {
            console.error("❌ Support socket connection error:", err);
            console.error("   Error details:", {
                message: err.message,
                type: err.type,
                description: err.description
            });
            isConnecting = false;
            toast.warning('Failed to connect to support service. Please check your connection.');
        });

        // Legacy channel: the server still emits `error` alongside
        // `supportError` for auth failures, so both go through the same check.
        socket.on("error", (error) => {
            console.error("❌ Support socket error:", error);
            if (handleAuthError(error)) return;
            toast.warning(error?.message || 'Support service error occurred');
        });

        // Reconnection lifecycle lives on the MANAGER (socket.io), not the
        // socket, in socket.io-client v4 — listeners on `socket` never fire.
        socket.io.on("reconnect_attempt", (attemptNumber) => {
            console.log(`🔄 Reconnection attempt #${attemptNumber}...`);
            // Carry the CURRENT token, not the one from the original handshake:
            // the HTTP interceptor may have refreshed it while we were away.
            const tk = localStorage.getItem('accessToken');
            if (tk) {
                // The token rides in `auth` only — never in the URL.
                socket.auth = { ...(socket.auth || {}), token: tk };
            }
        });

        socket.io.on("reconnect", (attemptNumber) => {
            console.log("🔄 Support socket RECONNECTED after", attemptNumber, "attempts", {
                socketId: socket?.id,
                role: connectionRole,
                userId: connectionId,
                timestamp: new Date().toISOString()
            });
            toast.success('Reconnected to support service');
            // Ticket re-request and room re-join happen in the `connect`
            // handler above, once the namespace is actually connected.
        });

        socket.io.on("reconnect_error", (err) => {
            console.error("❌ Reconnection error:", err);
        });

        socket.io.on("reconnect_failed", () => {
            console.error("❌ Reconnection failed after all attempts");
            toast.warning('Unable to reconnect to support service. Please refresh the page.');
        });

        return socket;
    } catch (error) {
        console.error('Error creating support socket:', error);
        isConnecting = false;
        throw error;
    }
};

/**
 * Get existing socket instance
 * @returns {Object|null} Socket instance or null
 */
export const getSupportSocket = () => {
    if (!socket && !socketWarningShown) {
        // Only warn once to avoid console spam
        socketWarningShown = true;
        console.log('ℹ️ Support socket not initialized yet. It will be created automatically.');
    }
    return socket;
};

/**
 * Disconnect support socket
 */
export const disconnectSupportSocket = () => {
    if (socket) {
        console.log('🔌 Disconnecting support socket:', socket.id);
        console.log('   Removing all listeners and closing connection');
        socket.removeAllListeners();
        socket.io?.removeAllListeners?.();
        socket.disconnect();
        socket = null;
        isConnecting = false;
        connectionRole = null;
        connectionId = null;
        connectCount = 0;
        console.log('✅ Socket disconnected and cleaned up');
    } else {
        console.log('⚠️ No socket to disconnect');
    }
};

/**
 * Check if socket is connected
 * @returns {boolean}
 */
export const isSupportSocketConnected = () => {
    return socket?.connected || false;
};

/**
 * Manually reconnect the socket
 * @returns {boolean} - true if reconnection initiated, false if no socket exists
 */
export const reconnectSupportSocket = () => {
    if (socket && !socket.connected) {
        console.log('🔄 Manually reconnecting support socket...');
        console.log('   Previous role:', connectionRole, 'ID:', connectionId);
        socket.connect();
        return true;
    } else if (!socket) {
        console.warn('⚠️ No socket instance to reconnect. Use connectSupportSocket() instead.');
        return false;
    } else {
        console.log('✅ Socket already connected');
        return false;
    }
};

// Raiser-side socket events. Every tenant-app login (admin, doctor, staff,
// patient) raises tickets here; the handler side (queues, accept / assign /
// reassign) lives in the Super Admin app.
export const userSocketEvents = {
    // Request tickets manually (for when component mounts after socket connected)
    requestTickets: () => {
        if (socket) {
            if (socket.connected) {
                console.log('📤 EMIT: requestUserTickets (socket connected)');
                socket.emit('requestUserTickets');
            } else {
                console.warn('⚠️ Cannot request tickets: socket not connected');
                // Wait for connection and then request
                socket.once('connect', () => {
                    console.log('📤 EMIT: requestUserTickets (after connection)');
                    socket.emit('requestUserTickets');
                });
            }
        } else {
            console.error('❌ Cannot request tickets: socket not available');
        }
    },

    // Listen for ticket lists
    onUserTickets: (callback) => {
        if (socket) {
            socket.off('userTickets');
            socket.on('userTickets', (data) => {
                console.log('📥 LISTEN: userTickets', data);
                callback(data);
            });
        }
    },
    onUserTicketListUpdate: (callback) => {
        if (socket) { socket.off('userTicketListUpdate'); socket.on('userTicketListUpdate', callback); }
    },

    // Initialize ticket
    initTicket: (data) => {
        if (socket) {
            console.log('📤 EMIT: initSupportTicket', data);
            socket.emit('initSupportTicket', data);
        }
    },
    onTicketInitialized: (callback) => {
        if (socket) {
            socket.off('supportTicketInitialized');
            socket.on('supportTicketInitialized', (ticket) => {
                console.log('📥 LISTEN: supportTicketInitialized', ticket);
                callback(ticket);
            });
        }
    },

    // Join ticket. Membership is checked server-side against the socket's
    // verified identity, so the ticket id is all it needs.
    joinTicket: (ticketId) => {
        if (socket) socket.emit('joinSupportTicket', { ticketId });
    },
    onTicketJoined: (callback) => {
        if (socket) { socket.off('supportTicketJoined'); socket.on('supportTicketJoined', callback); }
    },

    // Send message: { ticketId, message, replyTo? }. The sender is the socket's
    // verified identity, so nothing about "who" travels in the payload.
    sendMessage: (data) => {
        if (socket) {
            console.log('📤 USER: Emitting sendSupportMessage:', data);
            socket.emit('sendSupportMessage', data);
        } else {
            console.error('❌ USER: Socket not available when trying to send message');
        }
    },
    onReceiveMessage: (callback) => {
        const wrappedCallback = (data) => {
            console.log('📥 USER: Received receiveSupportMessage event:', data);
            callback(data);
        };

        if (socket && socket.connected) {
            socket.off('receiveSupportMessage');
            console.log('🎧 USER: Attaching receiveSupportMessage listener (socket connected)');
            socket.on('receiveSupportMessage', wrappedCallback);
        } else if (socket && socket.connecting) {
            console.log('⏳ USER: Socket connecting, storing callback to attach when ready');
            pendingCallbacks.user.receiveMessage = wrappedCallback;
            // Also attach when socket connects
            socket.once('connect', () => {
                socket.off('receiveSupportMessage');
                console.log('🎧 USER: Socket connected, attaching receiveSupportMessage listener');
                socket.on('receiveSupportMessage', wrappedCallback);
            });
        } else {
            console.log('⏳ USER: Socket not available, storing callback');
            pendingCallbacks.user.receiveMessage = wrappedCallback;
        }
    },

    // Ticket status changes
    onTicketAssigned: (callback) => {
        if (socket) { socket.off('ticketAssigned'); socket.on('ticketAssigned', callback); }
    },
    onTicketStatusChanged: (callback) => {
        if (socket) { socket.off('ticketStatusChanged'); socket.on('ticketStatusChanged', callback); }
    },

    // Close ticket
    closeTicket: (ticketId) => {
        if (socket) {
            console.log('📤 EMIT: closeSupportTicket', { ticketId });
            socket.emit('closeSupportTicket', { ticketId });
        }
    },
    onTicketClosed: (callback) => {
        if (socket) { socket.off('supportTicketClosed'); socket.on('supportTicketClosed', callback); }
    },

    // Duplicate ticket prevention
    onTicketAlreadyOpen: (callback) => {
        if (socket) {
            socket.off('supportTicketAlreadyOpen');
            socket.on('supportTicketAlreadyOpen', (data) => {
                console.log('📥 LISTEN: supportTicketAlreadyOpen', data);
                callback(data);
            });
        }
    },
    // The service keeps the real listener (it must see auth errors first);
    // the page's callback receives everything that was not an auth error.
    onSupportError: (callback) => {
        pageSupportErrorHandler = typeof callback === 'function' ? callback : null;
    },

    // Fetch message history
    fetchHistory: (ticketId, page = 1) => {
        if (socket) socket.emit('fetchSupportHistory', { ticketId, page });
    },
    onHistory: (callback) => {
        if (socket) { socket.off('supportHistory'); socket.on('supportHistory', callback); }
    },

    // Run `callback` after every RE-connect (used by components to re-join
    // rooms / re-fetch data). Returns an unsubscribe function; removeListener
    // ('reconnect') clears all of them.
    onReconnect: (callback) => {
        if (typeof callback !== 'function') return () => {};
        reconnectHandlers.add(callback);
        return () => reconnectHandlers.delete(callback);
    },

    // Remove listeners
    removeListener: (event) => {
        if (event === 'supportError') { pageSupportErrorHandler = null; return; }
        if (event === 'reconnect') { reconnectHandlers.clear(); return; }
        if (socket) socket.off(event);
    }
};

export default {
    connectSupportSocket,
    getSupportSocket,
    disconnectSupportSocket,
    isSupportSocketConnected,
    reconnectSupportSocket,
    userSocketEvents
};

